import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import {
  CellularBillingRow,
  CellularBillingSource
} from 'src/app/shared/model/cellular-billing';

@Component({
  selector: 'app-cellular-usage-table',
  templateUrl: './cellular-usage-table.component.html',
  styleUrls: ['./cellular-usage-table.component.scss']
})
export class CellularUsageTableComponent implements AfterViewInit, OnChanges {
  @Input() rows: CellularBillingRow[] = [];
  @Input() readOnly = false;
  @Input() duplicatesOnly = false;
  @Output() duplicatesOnlyChange = new EventEmitter<boolean>();
  @Output() edited = new EventEmitter<void>();
  duplicateMacs: string[] = [];
  duplicateRowCount = 0;
  @ViewChild(MatSort, { static: false }) sort: MatSort;

  identifiersView = false;
  readonly billingColumns = [
    'mac',
    'tealUsageGb', 'koreUsageGb', 'cpUsageGb', 'verificationSource', 'verificationGb',
    'gbAt95Percent', 'gigsToCharge', 'bill', 'networkPlan', 'remarks'
  ];
  readonly identifierColumns = ['mac', 'licenseIccid', 'eid', 'tealBootstrapIccid', 'simSid', 'koreIccid'];
  get displayedColumns(): string[] { return this.identifiersView ? this.identifierColumns : this.billingColumns; }
  identifiersDiffer(row: CellularBillingRow): boolean {
    return new Set([row.licenseIccid, row.tealBootstrapIccid, row.koreIccid].filter(Boolean)).size > 1;
  }
  readonly verificationSources: Array<{ value: CellularBillingSource, label: string }> = [
    { value: 'teal', label: 'Teal' },
    { value: 'kore', label: 'KORE' },
    { value: 'cp', label: 'License / CP' }
  ];
  readonly dataSource = new MatTableDataSource<CellularBillingRow>([]);
  searchValue = '';

  constructor() {
    this.dataSource.filterPredicate = (row) => {
      if (!(Number(row.verificationGb) >= 0.001)) return false;
      const searchable = [
        row.mac, row.eid, row.simSid, row.licenseIccid, row.tealBootstrapIccid,
        row.koreIccid, row.deviceGroup, row.deviceName, row.firmwareVersion,
        row.networkPlan, row.remarks
      ].join(' ').toLowerCase();
      return (!this.duplicatesOnly || row.hasDuplicateMac) && searchable.indexOf(this.searchValue.trim().toLowerCase()) > -1;
    };
    this.dataSource.sortingDataAccessor = (row, property) => {
      const value = row[property];
      return typeof value === 'string' ? value.toLowerCase() : value;
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.rows) {
      this.dataSource.data = this.rows || [];
      const duplicates = (this.rows || []).filter(row => row.hasDuplicateMac);
      this.duplicateMacs = Array.from(new Set(duplicates.map(row => row.mac).filter(Boolean))).sort();
      this.duplicateRowCount = duplicates.length;
    }
    this.refreshFilter();
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
  }

  applySearch(value: string): void {
    this.searchValue = value;
    this.refreshFilter();
  }

  toggleDuplicates(value: boolean): void {
    this.duplicatesOnly = value;
    this.duplicatesOnlyChange.emit(value);
    this.refreshFilter();
  }

  viewDuplicateMac(mac: string): void {
    this.searchValue = mac;
    this.toggleDuplicates(true);
  }

  private refreshFilter(): void {
    this.dataSource.filter = JSON.stringify({ search: this.searchValue, duplicatesOnly: this.duplicatesOnly });
  }

  updateVerification(row: CellularBillingRow): void {
    row.verificationGb = this.round(row[`${row.verificationSource}UsageGb`]);
    row.gbAt95Percent = this.round((row.verificationGb - 1) * 0.95);
    row.gigsToCharge = row.bill ? Math.max(Math.ceil(row.gbAt95Percent), 0) : 0;
    this.refreshFilter();
    this.edited.emit();
  }

  updateBill(row: CellularBillingRow): void {
    row.gigsToCharge = row.bill ? Math.ceil(row.gbAt95Percent) : 0;
    this.edited.emit();
  }

  formatGb(value: number): string {
    return Number(value || 0).toFixed(3);
  }

  trackByRow(index: number, row: CellularBillingRow): string {
    return row.rowId || `${row.mac}:${index}`;
  }

  private round(value: number): number {
    return Math.round((Number(value || 0) + Number.EPSILON) * 1000000) / 1000000;
  }
}
